import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import ExpenseDetailsPanel from '../../ExpensesPage/ExpenseDetailsPanel';
import ApproveExpenseModal
  from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/ApproveExpenseModal/ApproveExpenseModal';
import RejectExpenseModal
  from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal/RejectExpenseModal';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {subscriptionActions} from '../../../../state/actions/subscription.actions';
import {expensesHelpers, reviewHelpers, systemHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {firebaseEvents} from '../../../../snippets/firebase';
import {DEFAULT_MODAL_PROPS} from '../../../../constants';

const TransactionExpenseDetailsPanel = ({
  onClose,
  onExpenseUpdate,
  onSubscriptionUpdate,
  subscription,
  transaction,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses', 'transactions']);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [expenses, setExpenses] = React.useState([]);

  const [approveModalProps, setApproveModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [rejectModalProps, setRejectModalProps] = useState(DEFAULT_MODAL_PROPS);

  const dispatch = useDispatch();

  const {employeeEmail} = useSelector(({
    user: { employee }
  }) => ({
    employeeEmail: employee?.email
  }));

  const expenseId = transaction?.expense_id;

  const expenseSubscription = useMemo(() => {
    if (subscription) return subscription;

    const subscriptionId = transaction?.expense?.subscription_id;
    if (!subscriptionId) return null;
    return subscriptions.find(s => s.id === subscriptionId);
  }, [subscription, transaction?.expense, subscriptions]);

  const expense = useMemo(() => {
    const expenseId = transaction?.expense_id;
    if (!expenseId) return null;
    return expenses.find(e => e.id === expenseId);
  }, [transaction?.expense_id, expenses]);

  useEffect(() => {
    if (subscription) return;

    const subscriptionId = transaction?.expense?.subscription_id;
    if (!subscriptionId) return;

    const searchedSubscription = subscriptions.find(s => s.id === subscriptionId);
    if (searchedSubscription) return;

    loadSubscription(subscriptionId);
  }, [transaction?.expense?.subscription_id, subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const expenseId = transaction?.expense_id;
    if (!expenseId) return;

    const expense = expenses.find(e => e.id === expenseId);
    if (expense) return;

    loadExpense(expenseId);
  }, [transaction?.expense_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscription = (subscriptionId) => {
    dispatch(subscriptionActions.getSubscription(
      subscriptionId,
      (subscription) => setSubscriptions([...subscriptions, subscription])
    ));
  }

  const loadExpense = (id) => {
    dispatch(expenseActions.getExpense({
      id,
      successCallback: (expense) => setExpenses([...expenses, expense])
    }));
  }

  const successChangeInvoiceHandle = (response, successCallback) => {
    successCallback?.(response);
    systemHelpers.logEvent(firebaseEvents.TRANSACTIONS_UPDATE_TRANSACTION_INVOICE);
  }

  const handleUpdateExpense = (updatedExpense) => {
    setExpenses(expenses.map(e => e.id === updatedExpense.id ? updatedExpense : e));
    onExpenseUpdate?.(updatedExpense);
  }

  const handleInvoiceRemove = ({successCallback, errorCallback} = {}) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction,
      successCallback: (response) => successChangeInvoiceHandle(response, successCallback),
      errorCallback,
      unlinkExpense: (props) => dispatch(invoicesActions.unlinkExpense(props)),
      onExpenseUpdate: handleUpdateExpense
    });
  }

  const handleReviewEdit = () => {
    if (!expenseId) return;

    dispatch(expenseActions.reopenExpense({
      id: expenseId,
      successCallback: (response) => handleUpdateExpense(response),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKEy: 'reopenExpenseError', t}),
    }));
  }

  const handleReassignExpense = (e, data) => {
    if (!expenseId) return;

    dispatch(expenseActions.reassignExpense({
      id: expenseId,
      data,
      successCallback: (response) => handleUpdateExpense(response),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'reassignExpenseError', t}),
    }));
  }

  const handleDiscardAutoApproval = () => {
    if (!expenseId) return;

    dispatch(expenseActions.discardExpenseAutoApproval({
      id: expenseId,
      successCallback: (response) => handleUpdateExpense(response),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'discardAutoApprovalError', t}),
    }));
  }

  const handleCloseApproveModal = () => setApproveModalProps({...approveModalProps, open: false});

  const handleCloseRejectModal = () => setRejectModalProps({...rejectModalProps, open: false});

  const handleApproveExpense = ({data = {}, successCallback, errorCallback}) => {
    if (!expenseId) return;

    dispatch(expenseActions.approveExpense({
      id: expenseId,
      data,
      successCallback: (response) => {
        handleUpdateExpense(response);
        successCallback?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKey: 'approveExpenseError', t});
        errorCallback?.(response);
      },
    }));
  }

  const handleOkApproveModal = ({successCallback, errorCallback}) => {
    if (!expenseId) return;

    setApproveModalProps(prevState => ({ ...prevState, loading: true }));

    handleApproveExpense({
      data: { admin_override: true },
      successCallback: (response) => {
        handleCloseApproveModal();
        successCallback?.(response);
      },
      errorCallback
    });
  }

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    const id = rejectModalProps?.expense?.id;
    if (!id) return;

    setRejectModalProps(prevState => ({ ...prevState, loading: true }));

    dispatch(expenseActions.rejectExpense({
      id,
      data: {
        ...fields,
        ...(rejectModalProps?.data || {})
      },
      successCallback: (response) => {
        setRejectModalProps(DEFAULT_MODAL_PROPS);
        handleUpdateExpense(response);
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(err?.response);
      },
    }));
  }

  const handleOpenRejectModal = ({e, expense, data = {}, tableKey} = {}) => {
    e?.stopPropagation();
    setRejectModalProps({
      ...DEFAULT_MODAL_PROPS,
      open: true,
      expense,
      data,
      tableKey
    });
  }

  const handleEditExpense = (data) => {
    if (!expenseId) return;

    dispatch(expenseActions.updateExpense({
      id: expenseId,
      data,
      successCallback: handleUpdateExpense,
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'updateExpenseError', t}),
    }));
  }

  const handleApproveDetailsExpense = ({data}) => {
    const canQuickApprove = reviewHelpers.canQuickApprove({data: expense, employeeEmail});
    if (canQuickApprove) {
      handleApproveExpense({data});
    } else {
      setApproveModalProps({
        ...DEFAULT_MODAL_PROPS,
        open: true,
        expense,
      });
    }
  }

  const handleSubscriptionUpdate = (updatedSubscription) => {
    setSubscriptions(subscriptions.map(s => s.id === updatedSubscription.id ? updatedSubscription : s));
    onSubscriptionUpdate?.(updatedSubscription);
  }

  return (
    <>
      <ExpenseDetailsPanel
        expense={expense}
        className='expense-details-panel'
        defaultActivePanelKey={['transaction']}
        onApprove={handleApproveDetailsExpense}
        onClose={onClose}
        onDiscardAutoApproval={handleDiscardAutoApproval}
        onEdit={handleEditExpense}
        onInvoiceRemove={handleInvoiceRemove}
        onSubscriptionUpdate={handleSubscriptionUpdate}
        onUpdate={handleUpdateExpense}
        onReassign={handleReassignExpense}
        onReject={({e, data = {}}) => handleOpenRejectModal({data, e, expense})}
        onReviewEdit={handleReviewEdit}
        subscription={expenseSubscription}
        transaction={transaction}
        {...rest}
      />

      <RejectExpenseModal
        {...rejectModalProps}
        handleCancel={handleCloseRejectModal}
        handleOk={handleOkRejectModal}
      />

      <ApproveExpenseModal
        {...approveModalProps}
        handleCancel={handleCloseApproveModal}
        handleOk={handleOkApproveModal}
      />
    </>
  );
}

TransactionExpenseDetailsPanel.propTypes = {
  onClose: PropTypes.func,
  onExpenseUpdate: PropTypes.func,
  onSubscriptionUpdate: PropTypes.func,
  transaction: PropTypes.object,
}

export default TransactionExpenseDetailsPanel;
